import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  hrmsGetRequest,
  hrmsPostRequest,
  hrmsUpdateRequest,
} from "../services";
import toast from "react-hot-toast";

const initialState = {
  adminCheckIns: [],
  activeMidYearCheckIn: null,
  activeEndOfYearCheckIn: null,
  loading: false,
  error: null,
};

export const initiateAdminCheckIn = createAsyncThunk(
  "adminCheckIn/initiate",
  async (
    { adminEmail, adminName, initiatedByEmail, checkInType, goalPlanningId },
    { rejectWithValue }
  ) => {
    try {
      const response = await hrmsPostRequest("/api/admin-check-ins/initiate", {
        adminEmail,
        adminName,
        initiatedByEmail,
        checkInType,
        goalPlanningId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message || error.message || "Failed to initiate check-in"
      );
    }
  }
);

export const fetchAdminCheckInsByEmail = createAsyncThunk(
  "adminCheckIn/fetchByEmail",
  async (adminEmail, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(
        `/api/admin-check-ins/email/${encodeURIComponent(adminEmail)}`
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchActiveAdminCheckIn = createAsyncThunk(
  "adminCheckIn/fetchActive",
  async ({ adminEmail, checkInType }, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(
        `/api/admin-check-ins/active/${encodeURIComponent(adminEmail)}/${checkInType}`
      );
      return { checkInType, data: response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const uploadCheckInEvidence = createAsyncThunk(
  "adminCheckIn/uploadEvidence",
  async (files, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));

      const response = await hrmsPostRequest(
        "/api/admin-check-ins/upload-evidence",
        formData,
        true
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message || error.message || "Failed to upload evidence"
      );
    }
  }
);

export const saveAdminDraftCheckIn = createAsyncThunk(
  "adminCheckIn/adminDraft",
  async ({ id, adminEmail, formData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/admin-check-ins/${id}/admin-draft`,
        { ...formData, adminEmail }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message ||
          error.message ||
          "Failed to save check-in draft"
      );
    }
  }
);

export const submitAdminCheckIn = createAsyncThunk(
  "adminCheckIn/adminSubmit",
  async ({ id, adminEmail, formData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/admin-check-ins/${id}/admin-submit`,
        { ...formData, adminEmail }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message || error.message || "Failed to submit check-in"
      );
    }
  }
);

export const submitConferenceCheckIn = createAsyncThunk(
  "adminCheckIn/conferenceSubmit",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/admin-check-ins/${id}/conference`,
        formData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message ||
          error.message ||
          "Failed to submit check-in conference"
      );
    }
  }
);

export const reinitiateAdminCheckIn = createAsyncThunk(
  "adminCheckIn/reinitiate",
  async (id, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/admin-check-ins/${id}/reinitiate`,
        {}
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message || error.message || "Failed to reinitiate check-in"
      );
    }
  }
);

const adminCheckInSlice = createSlice({
  name: "adminCheckIn",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(initiateAdminCheckIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(initiateAdminCheckIn.fulfilled, (state, action) => {
        state.loading = false;
        const existingIndex = state.adminCheckIns.findIndex(
          (item) => item.id === action.payload.id
        );
        if (existingIndex !== -1) {
          state.adminCheckIns[existingIndex] = action.payload;
        } else {
          state.adminCheckIns = [action.payload, ...state.adminCheckIns];
        }
        toast.success("Check-in initiated successfully");
      })
      .addCase(initiateAdminCheckIn.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })
      .addCase(fetchAdminCheckInsByEmail.fulfilled, (state, action) => {
        state.adminCheckIns = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchActiveAdminCheckIn.fulfilled, (state, action) => {
        if (action.payload.checkInType === "mid_year") {
          state.activeMidYearCheckIn = action.payload.data;
        } else {
          state.activeEndOfYearCheckIn = action.payload.data;
        }
      })
      .addCase(saveAdminDraftCheckIn.fulfilled, (state, action) => {
        const existingIndex = state.adminCheckIns.findIndex(
          (item) => item.id === action.payload.id
        );
        if (existingIndex !== -1) {
          state.adminCheckIns[existingIndex] = action.payload;
        } else {
          state.adminCheckIns = [action.payload, ...state.adminCheckIns];
        }
        if (action.payload.checkInType === "mid_year") {
          state.activeMidYearCheckIn = action.payload;
        } else {
          state.activeEndOfYearCheckIn = action.payload;
        }
        toast.success("Progress saved. You can continue later.");
      })
      .addCase(saveAdminDraftCheckIn.rejected, (_, action) => {
        toast.error(action.payload);
      })
      .addCase(submitAdminCheckIn.fulfilled, (state, action) => {
        const existingIndex = state.adminCheckIns.findIndex(
          (item) => item.id === action.payload.id
        );
        if (existingIndex !== -1) {
          state.adminCheckIns[existingIndex] = action.payload;
        }
        if (action.payload.checkInType === "mid_year") {
          state.activeMidYearCheckIn = null;
        } else {
          state.activeEndOfYearCheckIn = null;
        }
        toast.success("Check-in submitted successfully");
      })
      .addCase(submitAdminCheckIn.rejected, (_, action) => {
        toast.error(action.payload);
      })
      .addCase(submitConferenceCheckIn.fulfilled, (state, action) => {
        const existingIndex = state.adminCheckIns.findIndex(
          (item) => item.id === action.payload.id
        );
        if (existingIndex !== -1) {
          state.adminCheckIns[existingIndex] = action.payload;
        }
        toast.success("Check-in conference completed");
      })
      .addCase(submitConferenceCheckIn.rejected, (_, action) => {
        toast.error(action.payload);
      })
      .addCase(reinitiateAdminCheckIn.fulfilled, (state, action) => {
        const existingIndex = state.adminCheckIns.findIndex(
          (item) => item.id === action.payload.id
        );
        if (existingIndex !== -1) {
          state.adminCheckIns[existingIndex] = action.payload;
        } else {
          state.adminCheckIns = [action.payload, ...state.adminCheckIns];
        }
        if (action.payload.checkInType === "mid_year") {
          state.activeMidYearCheckIn = action.payload;
        } else {
          state.activeEndOfYearCheckIn = action.payload;
        }
        toast.success(
          "Form reinitiated and sent back to admin for updates"
        );
      })
      .addCase(reinitiateAdminCheckIn.rejected, (_, action) => {
        toast.error(action.payload);
      })
      .addCase(uploadCheckInEvidence.rejected, (_, action) => {
        toast.error(action.payload);
      });
  },
});

export default adminCheckInSlice.reducer;
